import type { Web3TxMultiCalculateRoutes } from "@/hooks/tx-flow/useDepositCalculate";
import type { KeyedMutator } from "swr";
import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from "react";

import moment from "moment";
import { SupRefresh, SupTXStuck } from "@repo/core-ui";
import { SupProgress, SupTooltip } from "@repo/core-ui";

interface Web3CommonProgressProps {
  calculateTimestamp: number;
  pauseRequests: boolean;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  mutate: KeyedMutator<Web3TxMultiCalculateRoutes[]>;
}

const interval = 90;

const setProgress = (value: number) => {
  return moment(value).add(interval, "seconds").diff(moment(), "seconds");
};

export default function Web3CommonProgress({ calculateTimestamp, setPauseRequests, pauseRequests, mutate }: Web3CommonProgressProps) {
  const [value, setValue] = useState<number>(setProgress(calculateTimestamp));

  useEffect(() => {
    const interval = setInterval(() => {
      if (!pauseRequests) {
        setValue(setProgress(calculateTimestamp));
      }
    }, 100);
    return () => clearInterval(interval);
  }, [calculateTimestamp, pauseRequests]);

  useEffect(() => {
    if (value <= 1) {
      mutate();
    }
  }, [mutate, value]);

  const clickHandler = useCallback(() => {
    setPauseRequests(false);
    mutate();
  }, [mutate, setPauseRequests]);

  return (
    <SupTooltip
      color="primary"
      placement="left"
      content={pauseRequests ? "Routes refresh is paused. Click to refresh" : `Routes refresh every ${interval} seconds. Click to refresh`}
    >
      <div className="group mr-2 flex h-5 w-5 items-center justify-center hover:cursor-pointer" onClick={clickHandler}>
        {pauseRequests ? (
          <SupTXStuck className="block group-hover:hidden" />
        ) : (
          <SupProgress className="block h-5 w-5 group-hover:hidden" value={value} maxValue={interval} color="primary" />
        )}
        <SupRefresh className="hidden group-hover:block" />
      </div>
    </SupTooltip>
  );
}
